import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import Stepper from '@pelckmans/elementis/components/stepper';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '../../../../../api/groups';
import { registerMembers } from '../../../../../api/my-groups';
import ModalContext from '../../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../../utils/elementisUtils';
import LinkMembersData from './components/LinkMembersData';
import MembersFileImport from './components/MembersFileImport';
import ValidateMembersData from './components/ValidateMembersData';
import ImportMembersContextProvider, {
  ErrorFields,
  ImportMembersContext,
  Steps,
} from './contexts/ImportMembersContext';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.IMPORT_MEMBERS';

interface Props {
  id: string;
  group: Group;
  currentStep: string;
  refetch: () => void;
}

function ImportMembersModal({ id, group, currentStep, refetch }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { setAlertType, alertType, goToNextStep, stepperProgress, toBeAddedMembers, selection, errors } =
    useContext(ImportMembersContext);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setAlertType(undefined);
  }, [currentStep, setAlertType]);

  useEffect(() => {
    if (currentStep === Steps.DATA_VERIFICATION) {
      const hasErrorsInRows = Boolean(
        errors && Object.keys(errors).filter(key => key !== ErrorFields.UPLOAD_FIELD).length > 0,
      );
      if (hasErrorsInRows) {
        setAlertType(() => 'INVALID_DATA_WARNING');
      }
    }
  }, [currentStep, errors, setAlertType]);

  function renderAlert() {
    if (alertType === undefined) return undefined;

    let message = '';
    switch (alertType) {
      case 'NO_REQUIRED_COLUMNS':
        message = t(`${TRANSLATION_KEY}.LINK_MEMBERS_DATA.ALERTS.NO_REQUIRED_COLUMNS`);
        break;
      case 'NO_ROWS_SELECTED':
      case 'INVALID_DATA_WARNING':
      case 'SELECTED_INVALID_ROWS':
        message = t(`${TRANSLATION_KEY}.VALIDATE_MEMBERS_DATA.ALERTS.${alertType}`);
        break;
      default:
        message = t(`ALERTS.${alertType}.MESSAGE`);
    }

    return buildGroupProps(
      <Alert
        prominence={alertType === 'INVALID_DATA_WARNING' ? 'warning' : 'danger'}
        {...(alertType === 'RETRY'
          ? {
              actions: {
                'aria-label': undefined,
                role: undefined,
                children: (
                  <Button prominence="danger" onClick={handleNextStep}>
                    {t('ALERTS.RETRY.ACTION')}
                  </Button>
                ),
              },
            }
          : {})}
      >
        <span dangerouslySetInnerHTML={{ __html: message }}></span>
      </Alert>,
    );
  }

  async function handleNextStep() {
    if (currentStep !== Steps.DATA_VERIFICATION) {
      goToNextStep(currentStep);
      return;
    }

    if (selection.size === 0) {
      setAlertType('NO_ROWS_SELECTED');
      return;
    }

    const hasErrors = Array.from(selection).some(index => errors?.[`${index}`]);
    if (hasErrors) {
      setAlertType('SELECTED_INVALID_ROWS');
      return;
    }

    try {
      setIsLoading(true);

      const selectedMembers = toBeAddedMembers.filter((_, index) => selection.has(index));
      await registerMembers(group.id, selectedMembers);

      refetch();
      closeModal(id);
    } catch (e) {
      setAlertType('RETRY');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog id={id} aria-label={t(`${TRANSLATION_KEY}.A11Y_LABEL`)} modal onClose={() => closeModal(id)} size="md">
      <Dialog.Header
        aria-label={t(`${TRANSLATION_KEY}.HEADER.CLOSE.A11Y_LABEL`)}
        onClose={() => closeModal(id)}
        title={t(`${TRANSLATION_KEY}.HEADER.TITLE`, { groupName: group.name })}
      >
        <Stepper.StepList aria-label={t(`${TRANSLATION_KEY}.HEADER.STEPS.A11Y_LABEL`)}>
          <Stepper.Step id="file-import" completed={stepperProgress[Steps.FILE_IMPORT]}>
            {t(`${TRANSLATION_KEY}.HEADER.STEPS.FILE_IMPORT`)}
          </Stepper.Step>
          <Stepper.Step
            id="column-mapping"
            disabled={!stepperProgress[Steps.FILE_IMPORT]}
            completed={stepperProgress[Steps.COLUMN_MAPPING]}
          >
            {t(`${TRANSLATION_KEY}.HEADER.STEPS.COLUMN_MAPPING`)}
          </Stepper.Step>
          <Stepper.Step id="data-verification" disabled={!stepperProgress[Steps.COLUMN_MAPPING]}>
            {t(`${TRANSLATION_KEY}.HEADER.STEPS.DATA_VERIFICATION`)}
          </Stepper.Step>
        </Stepper.StepList>
      </Dialog.Header>
      <Dialog.Body alertGroupProps={renderAlert()} className="dialog-body">
        <Stepper.StepContent>
          <Stepper.StepPanel id="file-import">
            <MembersFileImport />
          </Stepper.StepPanel>
          <Stepper.StepPanel id="column-mapping">
            <LinkMembersData />
          </Stepper.StepPanel>
          <Stepper.StepPanel id="data-verification">
            <ValidateMembersData />
          </Stepper.StepPanel>
        </Stepper.StepContent>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={() => closeModal(id)}>
              {t('ACTIONS.CANCEL')}
            </Button>

            <Button prominence="success" key="next" onClick={handleNextStep} busy={isLoading}>
              {currentStep === Steps.DATA_VERIFICATION ? t('ACTIONS.CONFIRM') : t('ACTIONS.NEXT')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}

interface WrappedImportMembersModalProps extends Omit<Props, 'currentStep'> {
  existingUsernames: string[];
}

export default function WrappedImportMembersModal(props: WrappedImportMembersModalProps) {
  const [currentStep, setCurrentStep] = useState<string>(Steps.FILE_IMPORT);

  return (
    <Stepper onStepChange={setCurrentStep} selectedStep={currentStep}>
      <ImportMembersContextProvider existingUsernames={props.existingUsernames}>
        <ImportMembersModal {...props} currentStep={currentStep} />
      </ImportMembersContextProvider>
    </Stepper>
  );
}
